// @ts-nocheck
/*
 * Port of views/dashboard/credit-history/summary.js (458 lines). BYTE-IDENTICAL.
 *
 * Per-company credit summary page. Pick a company + date range, hit Filter,
 * see 6 summary cards (voice/tts/inbound/outbound addition and deduction),
 * bar + pie charts for non-zero summary values, and a records table.
 *
 * PrimeReact Dropdown â†’ react-bootstrap Form.Select; PrimeReact
 * DataTable â†’ obd3-gui DataTable; PrimeReact Button â†’ react-bootstrap.
 *
 * v1 quirks preserved:
 *  - `handleClear` also refetches companies (side effect: shows a Loader
 *    briefly). Kept exactly.
 *  - Chart panel only shown when `selectedCompany && summary` and any
 *    numeric value in summary > 0. When zero'd, no chart at all.
 *  - `formatDate` uses `date.toISOString().split('T')[0]` — differs from
 *    the list page which uses local YYYY-MM-DD. Kept both verbatim; the
 *    UTC vs local skew is a v1 behaviour, not a bug to fix.
 *  - `summary?.[item.key] || 0` displays without formatRupeeAmount on
 *    the top cards (bare ₹number, unformatted). Deliberate v1 shape.
 */
import React, { useEffect, useState } from 'react';
import { Card as ShadCard } from '@/components/ui/card';
import { Button as _ShadButton } from '@/components/ui/button';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const Card: any = ({ className, children, ...props }: any) => (
  <ShadCard className={className} {...props}>
    {children}
  </ShadCard>
);
Card.Header = ({ className, children, ...props }: any) => (
  <div className={_cx('card-header', className)} {...props}>
    {children}
  </div>
);
Card.Body = ({ className, children, ...props }: any) => (
  <div className={_cx('card-body', className)} {...props}>
    {children}
  </div>
);
Card.Title = ({ as: As = 'h5', className, children, ...props }: any) => (
  <As className={_cx('card-title', className)} {...props}>
    {children}
  </As>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
Form.Check = ({
  type = 'checkbox',
  label,
  id,
  className,
  inline,
  children,
  ...props
}: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
    {children}
  </div>
);
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/data-table/DataTable';
import { getApiCall } from '@/service/httpCall';
import Loader from '@/components/partials/components/Loader';
import {
  hasPermission,
  formatRupeeAmount,
} from '@/utilities/helpers';
import { getUser } from '@/utilities/setting';
import {
  FaPlus,
  FaMinus,
  FaExchangeAlt,
  FaMicrophone,
  FaArrowUp,
  FaArrowDown,
  FaRobot,
  FaBullhorn,
} from 'react-icons/fa';
import { RefreshCw } from 'lucide-react';
import {
  API_COMPANY_LIST,
  API_CREDIT_HISTORY_SUMMARY_OF_SPECIFIC_COMPANY,
} from '@/service/url';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { DateRangeFilter } from '@/components/filters/DateRangeFilter';
import { callColumns } from './callColumns';

const COLORS = [
  '#28a745',
  '#dc3545',
  '#20c997',
  '#fd7e14',
  '#007bff',
  '#6f42c1',
  '#e83e8c',
  '#6610f2',
  '#17a2b8',
];

const EMPTY_SUMMARY = {
  addition: 0,
  deduction: 0,
  tts_addition: 0,
  tts_deduction: 0,
  inbound_deduction: 0,
  outbound_deduction: 0,
  ai_agent_addition: 0,
  ai_agent_deduction: 0,
  campaign_deduction: 0,
};

const dateToInputStr = (d: any) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const MAX_RANGE_MONTHS = 3;
const earliestAllowedDate = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - MAX_RANGE_MONTHS);
  d.setHours(0, 0, 0, 0);
  return d;
};

const inputStrToLocalDate = (str: any) => {
  if (!str) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
  if (!m) {
    const d = new Date(str);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return new Date(
    parseInt(m[1], 10),
    parseInt(m[2], 10) - 1,
    parseInt(m[3], 10),
  );
};

const SummaryPage = () => {
  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = usePersistedState<any>('credit-history/CreditSummary:selectedCompany', null);

  const [records, setRecords] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(EMPTY_SUMMARY);

  const [startDate, setStartDate] = usePersistedState<any>('credit-history/CreditSummary:startDate', null);
  const [endDate, setEndDate] = usePersistedState<any>('credit-history/CreditSummary:endDate', null);

  const [loading, setLoading] = useState(false);

  const summaryConfig = [
    {
      key: 'addition',
      label: 'Voice Addition',
      icon: <FaPlus />,
      color: '#28a745',
    },
    {
      key: 'deduction',
      label: 'Voice Deduction',
      icon: <FaMinus />,
      color: '#dc3545',
    },
    {
      key: 'tts_addition',
      label: 'TTS Addition',
      icon: <FaMicrophone />,
      color: '#20c997',
    },
    {
      key: 'tts_deduction',
      label: 'TTS Deduction',
      icon: <FaMicrophone />,
      color: '#fd7e14',
    },
    {
      key: 'inbound_deduction',
      label: 'Inbound Deduction',
      icon: <FaExchangeAlt />,
      color: '#007bff',
    },
    {
      key: 'outbound_deduction',
      label: 'Outbound Deduction',
      icon: <FaExchangeAlt />,
      color: '#6f42c1',
    },
    {
      key: 'ai_agent_addition',
      label: 'AI Agent Addition',
      icon: <FaRobot />,
      color: '#e83e8c',
    },
    {
      key: 'ai_agent_deduction',
      label: 'AI Agent Deduction',
      icon: <FaRobot />,
      color: '#6610f2',
    },
    {
      key: 'campaign_deduction',
      label: 'Campaign Deduction',
      icon: <FaBullhorn />,
      color: '#17a2b8',
    },
  ];

  const fetchCompanies = async () => {
    const resp: any = await getApiCall(API_COMPANY_LIST);
    setCompanies(resp || []);
  };

  const fetchSummary = async () => {
    if (!selectedCompany) return;

    setLoading(true);

    const start = formatDate(startDate);
    const end = formatDate(endDate);

    const url = `${API_CREDIT_HISTORY_SUMMARY_OF_SPECIFIC_COMPANY}/${selectedCompany}?startDate=${start}&endDate=${end}`;

    try {
      const resp: any = await getApiCall(url);
      setRecords(resp.records || []);
      setSummary(resp.summary || {});
    } catch {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

    // Restored range (if any) wins over the month-to-date default.
    setStartDate((prev: any) => prev || firstDay);
    setEndDate((prev: any) => prev || today);

    fetchCompanies();
  }, []);

  useEffect(() => {
    if (!selectedCompany || !startDate || !endDate) return;
    fetchSummary();
    // Fetch when a company is picked so charts + listing appear without
    // requiring an extra Filter click (QA path: Credits → Summary → company).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany]);

  const formatDate = dateToInputStr;

  const handleFilter = () => {
    fetchSummary();
  };

  const handleClear = async () => {
    setLoading(true);

    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

    setSelectedCompany(null);
    setStartDate(firstDay);
    setEndDate(today);
    setRecords([]);
    setSummary(EMPTY_SUMMARY);

    await fetchCompanies();

    setLoading(false);
  };

  const chartData = summary
    ? [
        { name: 'Voice Add', value: Number(summary.addition) || 0 },
        { name: 'Voice Deduct', value: Number(summary.deduction) || 0 },
        { name: 'TTS Add', value: Number(summary.tts_addition) || 0 },
        { name: 'TTS Deduct', value: Number(summary.tts_deduction) || 0 },
        { name: 'Inbound', value: Number(summary.inbound_deduction) || 0 },
        { name: 'Outbound', value: Number(summary.outbound_deduction) || 0 },
        { name: 'AI Add', value: Number(summary.ai_agent_addition) || 0 },
        { name: 'AI Deduct', value: Number(summary.ai_agent_deduction) || 0 },
        { name: 'Campaign', value: Number(summary.campaign_deduction) || 0 },
      ]
    : [];

  const pieChartData = chartData.filter((item) => Number(item.value) > 0);

  const RADIAN = Math.PI / 180;
  const renderPieLabel = ({
    cx,
    cy,
    midAngle,
    outerRadius,
    name,
    value,
  }: any) => {
    if (!value || Number(value) <= 0) return null;
    const radius = outerRadius + 28;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text
        x={x}
        y={y}
        fill="currentColor"
        className="fill-foreground"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={12}
        fontWeight={600}
      >
        {`${name}: ${Number(value).toFixed(2)}`}
      </text>
    );
  };

  const [
    moduleOperationsForReadPermissionCheck,
    setModuleOperationsForReadPermissionCheck,
  ] = useState<any>({});

  useEffect(() => {
    const user = getUser();
    if (user) {
      if (user?.acl_settings) {
        setModuleOperationsForReadPermissionCheck(
          parseModuleOperations(user.acl_settings.module_operations),
        );
      }
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const canRead = hasPermission(
    moduleOperationsForReadPermissionCheck,
    'Credit History',
    'read',
  );

  if (
    Object.keys(moduleOperationsForReadPermissionCheck).length > 0 &&
    !canRead
  ) {
    return (
      <div
        className="text-center p-4"
        style={{
          marginTop: '50px',
          marginBottom: '50px',
          fontSize: '16px',
        }}
      >
        No read permission found in assigned ACL
      </div>
    );
  }

  if (loading && records.length === 0) {
    return (
      <ScrollPage>
        <Loader />
      </ScrollPage>
    );
  }

  const columns: ColumnDef<any>[] = [
    {
      id: 'action',
      header: 'Action',
      size: 190,
      cell: ({ row }: any) => {
        const action = row.original.action;
        const labelMap: Record<string, string> = {
          addition: 'Voice Addition',
          deduction: 'Voice Deduction',
          tts_addition: 'TTS Addition',
          tts_deduction: 'TTS Deduction',
          inbound_deduction: 'Inbound Deduction',
          outbound_deduction: 'Outbound Deduction',
          campaign_deduction: 'Campaign Deduction',
          ai_agent_addition: 'AI Agent Addition',
          ai_agent_deduction: 'AI Agent Deduction',
        };
        const isAdd = action?.endsWith('addition');
        return (
          <span
            className={
              'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
              (isAdd
                ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
                : 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300')
            }
          >
            {isAdd ? <FaArrowUp size={10} /> : <FaArrowDown size={10} />}
            {labelMap[action] || action}
          </span>
        );
      },
    },
    {
      id: 'amount',
      header: 'Amount',
      cell: ({ row }: any) => (
        <span className="font-mono text-xs font-semibold tabular-nums text-foreground">
          {formatRupeeAmount(row.original.credits_rupees)}
        </span>
      ),
    },
    {
      id: 'balance',
      header: 'Balance',
      cell: ({ row }: any) => (
        <span className="font-mono text-xs tabular-nums text-foreground">
          {formatRupeeAmount(row.original.balance)}
        </span>
      ),
    },
    {
      accessorKey: 'created_at',
      header: 'Date',
      cell: ({ row }: any) => (
        <span className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
          {new Date(row.original.created_at).toLocaleString()}
        </span>
      ),
    },
    ...callColumns,
  ];

  return (
    <ScrollPage>
      <PageHeader
        title="Credit Summary"
        description="Company-level credit summary — allocations, deductions, and current balance snapshots."
        actions={
          <Button variant="outline" onClick={fetchSummary} disabled={loading || !selectedCompany}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        }
      />
      {/* No outer Card wrapper — DataTable renders its own rounded
          border container, so wrapping added a second visible edge. */}
      <div className="mb-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Company
            </label>
            <Form.Select
              value={selectedCompany || ''}
              onChange={(e) => setSelectedCompany(e.target.value || null)}
              style={{ height: '40px', width: '240px' }}
            >
              <option value="">Select Company</option>
              {companies.map((company: any) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </Form.Select>
          </div>
          <DateRangeFilter
            value={{ from: dateToInputStr(startDate), to: dateToInputStr(endDate) }}
            onChange={({ from, to }) => {
              setStartDate(inputStrToLocalDate(from));
              setEndDate(inputStrToLocalDate(to));
            }}
            min={dateToInputStr(earliestAllowedDate())}
            max={dateToInputStr(new Date())}
            onApply={handleFilter}
            onReset={handleClear}
          />
        </div>

        {/* Compact embossed stat strip — 6 chips on one row, wraps on narrow. */}
        {summaryConfig && (
          <div className="mb-4 flex flex-wrap gap-2">
            {summaryConfig.map((item: any, index: number) => {
              const toneMap: any = {
                '#28a745': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
                '#dc3545': 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300',
                '#20c997': 'bg-teal-100 text-teal-700 dark:bg-teal-950/40 dark:text-teal-300',
                '#fd7e14': 'bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300',
                '#007bff': 'bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300',
                '#6f42c1': 'bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300',
                '#e83e8c': 'bg-pink-100 text-pink-700 dark:bg-pink-950/40 dark:text-pink-300',
                '#6610f2': 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300',
                '#17a2b8': 'bg-cyan-100 text-cyan-700 dark:bg-cyan-950/40 dark:text-cyan-300',
              };
              return (
                <div
                  key={index}
                  className="flex items-center gap-2 rounded-md border border-border/50 bg-card px-3 py-2 text-xs shadow-neu-raised"
                >
                  <span
                    className={
                      'flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-sm ' +
                      toneMap[item.color]
                    }
                  >
                    {item.icon}
                  </span>
                  <div className="flex flex-col leading-tight">
                    <span className="text-[11px] text-muted-foreground">
                      {item.label}
                    </span>
                    <span className="text-sm font-bold text-foreground tabular-nums">
                      {formatRupeeAmount(summary?.[item.key] || 0)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

            {selectedCompany &&
              summary &&
              Object.values(summary).some(
                (val: any) => Number(val) > 0,
              ) && (
                <div className="mb-8 grid grid-cols-1 gap-6 overflow-visible lg:grid-cols-2">
                  <div className="min-w-0 overflow-visible">
                    <ResponsiveContainer width="100%" height={320}>
                      <BarChart
                        data={chartData}
                        margin={{ top: 16, right: 16, left: 8, bottom: 8 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                        <XAxis dataKey="name" stroke="#ccc" />
                        <YAxis stroke="#ccc" />
                        <Tooltip />
                        <Bar dataKey="value">
                          {chartData.map((entry: any, index: number) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="min-w-0 overflow-visible [&_.recharts-wrapper]:!overflow-visible [&_.recharts-surface]:overflow-visible">
                    <ResponsiveContainer width="100%" height={360}>
                      <PieChart
                        margin={{ top: 36, right: 96, bottom: 36, left: 96 }}
                        style={{ overflow: 'visible' }}
                      >
                        <Pie
                          data={pieChartData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={90}
                          label={renderPieLabel}
                          labelLine
                          isAnimationActive={false}
                        >
                          {pieChartData.map((entry: any, index: number) => {
                            const colorIndex = chartData.findIndex(
                              (item) => item.name === entry.name,
                            );
                            return (
                              <Cell
                                key={`pie-cell-${index}`}
                                fill={
                                  COLORS[
                                    (colorIndex >= 0 ? colorIndex : index) %
                                      COLORS.length
                                  ]
                                }
                              />
                            );
                          })}
                        </Pie>
                        <Legend />
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

        <div className="mt-2 flex min-h-[420px] flex-col">
          <h5 className="mb-3 text-sm font-semibold uppercase tracking-wide text-foreground">
            Listing
          </h5>
          <DataTable
            tableId="credit-summary"
            columns={columns}
            data={records}
            pageSize={10}
            searchPlaceholder="Search records…"
            emptyTitle="No credit records"
            disableExport
          />
        </div>
    </ScrollPage>
  );
};

export default SummaryPage;
